import { DeleteIcon } from '@/assets/svg'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Edit, Image as ImageIcon, Plus } from 'lucide-react'
import React from 'react'

export interface CategoryItem {
  id: string | number
  name: string
  count?: number
  countLabel?: string // e.g., "vendors", "categories"
  image_url?: string
  [key: string]: any // Allow additional properties
}

interface BusinessTypeCategoryProps {
  // Header props
  title: string
  icon?: React.ReactNode
  onAdd?: () => void
  addButtonText?: string
  showAddButton?: boolean

  // List props
  items?: CategoryItem[]
  selectedItems?: (string | number)[]
  onItemSelect?: (itemId: string | number) => void
  onItemDelete?: (item: CategoryItem) => void
  onItemEdit?: (item: CategoryItem) => void
  showCheckbox?: boolean
  showDelete?: boolean
  showEdit?: boolean
  isLoading?: boolean

  // Empty state props
  emptyStateText?: string
  emptyStateIcon?: React.ReactNode

  // Styling
  className?: string
  cardClassName?: string
  itemClassName?: string

  // Custom render props
  renderItem?: (item: CategoryItem) => React.ReactNode
  renderEmptyState?: () => React.ReactNode
}

const BusinessTypeCategory: React.FC<BusinessTypeCategoryProps> = ({
  title,
  icon,
  onAdd,
  addButtonText = 'Add',
  showAddButton = true,
  items = [],
  selectedItems = [],
  onItemSelect,
  onItemDelete,
  onItemEdit,
  showCheckbox = true,
  showDelete = true,
  showEdit = true,
  isLoading = false,
  emptyStateText,
  emptyStateIcon,
  className,
  cardClassName,
  itemClassName,
  renderItem,
  renderEmptyState,
}) => {
  const hasItems = items && items.length > 0

  const defaultEmptyState = () => (
    <div className='flex flex-col items-center justify-center py-12 px-4'>
      {emptyStateIcon && (
        <div className='mb-4 text-gray-400'>{emptyStateIcon}</div>
      )}
      <p className='text-sm text-gray-500 text-center'>
        {emptyStateText || 'No items found'}
      </p>
    </div>
  )

  const defaultRenderItem = (item: CategoryItem) => (
    <div
      className={cn(
        'rounded-[12px] p-3 sm:p-4 flex items-start gap-3 bg-white',
        itemClassName
      )}
    >
      {/* {showCheckbox && (
        <div className='pt-1'>
          <Checkbox
            checked={selectedItems.includes(item.id)}
            id={String(item.id)}
            name={''}
            onChange={() => onItemSelect?.(item.id)}
          />
        </div>
      )} */}

      <div className='flex-shrink-0'>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className='w-16 h-16 rounded-lg object-cover border border-gray-200'
          />
        ) : (
          <div className='w-16 h-16 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center'>
            <ImageIcon className='w-6 h-6 text-gray-400' />
          </div>
        )}
      </div>

      <div className='flex-1 min-w-0'>
        <h3 className='text-sm sm:text-base font-semibold text-gray-900 break-words'>
          {item.name}
        </h3>
        {item.count !== undefined && (
          <p className='text-xs sm:text-sm text-gray-500 mt-1'>
            Used by {item.count} {item.countLabel || 'vendors'}
          </p>
        )}
      </div>

      <div className='flex items-center gap-2 flex-shrink-0'>
        {showEdit && onItemEdit && (
          <Button
            variant='ghost'
            size='icon'
            onClick={() => onItemEdit(item)}
            className='text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-full'
            aria-label={`Edit ${item.name}`}
          >
            <Edit className='h-5 w-5' />
          </Button>
        )}
        {showDelete && onItemDelete && (
          <Button
            variant='ghost'
            size='icon'
            onClick={() => onItemDelete(item)}
            className='text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full'
            aria-label={`Delete ${item.name}`}
          >
            <DeleteIcon className='h-5 w-5 text-[#ED544E]' />
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <div className={cn('space-y-6 mt-4', className)}>
      <div className='bg-white rounded-[1.15rem] p-5 flex flex-col'>
        <div className='flex items-center justify-between mb-6'>
          <div className='flex items-center gap-2'>
            {icon && (
              <span className='bg-gray-50 p-2 rounded-full'>
                <figure>{icon}</figure>
              </span>
            )}
            <h2 className='text-[1.1rem] font-bold text-[#424242]'>{title}</h2>
          </div>
          {showAddButton && onAdd && (
            <Button
              onClick={onAdd}
              className='bg-primary hover:bg-primary/90 text-primary-foreground w-10 h-10 rounded-full p-0 flex items-center justify-center'
              aria-label={addButtonText}
            >
              <Plus className='h-5 w-5' />
            </Button>
          )}
        </div>

        <div className={cn('space-y-3 bg-[#FAFAFA] rounded-[12px] p-3 sm:p-4 overflow-y-auto h-[350px] custom-scroll', cardClassName)}>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-sm text-gray-500'>Loading...</div>
            </div>
          ) : hasItems ? (
            items.map((item) => (
              <div key={item.id}>
                {renderItem ? renderItem(item) : defaultRenderItem(item)}
              </div>
            ))
          ) : (
            <div>
              {renderEmptyState ? renderEmptyState() : defaultEmptyState()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BusinessTypeCategory
